/**
 * ValuationPage — age-based price schedules (feature #2). A schedule maps age
 * breakpoints to wholesale/retail prices; a growable's value steps up as it ages
 * (e.g. mango graft: 5mo $30/$50 → 18mo $100/$160 → 3yr $200/$300). Schedules
 * scope by item kind (plant|fruit) and optionally a variety or category; the
 * backend resolves most-specific-first. Backed by /api/v1/valuation/schedules.
 */
import { useState } from 'react'
import { apiError } from '../lib/errors'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, DollarSign } from 'lucide-react'
import { valuationApi, varietiesApi, type ValuationSchedule, type ValuationPoint } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', border: 'rgba(255,255,255,0.08)',
  amber: 'var(--color-primary)', green: '#2D6A2F', blue: '#5B8DEF',
  red: '#E5533C', text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
}

type Draft = {
  id?: string
  name: string
  item_kind: 'plant' | 'fruit'
  variety_id: string
  category: string
  points: ValuationPoint[]
}
const blank: Draft = { name: '', item_kind: 'plant', variety_id: '', category: '', points: [{ age_months: 0, wholesale_usd: null, retail_usd: null }] }

export function ValuationPage() {
  const qc = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)

  const schedules = useQuery({ queryKey: ['valuation'], queryFn: () => valuationApi.list().then(r => r.data.schedules) })
  const varieties = useQuery({ queryKey: ['varieties'], queryFn: () => varietiesApi.list().then(r => r.data.varieties) })

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['valuation'] }); setDraft(null) }
  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = {
        name: d.name, item_kind: d.item_kind,
        variety_id: d.variety_id || null, category: d.category,
        points: d.points.filter(p => p.wholesale_usd != null || p.retail_usd != null),
      }
      return d.id ? valuationApi.update(d.id, body) : valuationApi.create(body)
    },
    onSuccess: invalidate,
  })
  const remove = useMutation({ mutationFn: (id: string) => valuationApi.remove(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['valuation'] }) })

  const edit = (s: ValuationSchedule) => setDraft({
    id: s.id, name: s.name, item_kind: s.item_kind, variety_id: s.variety_id ?? '',
    category: s.category, points: s.points.length ? [...s.points].sort((a, b) => a.age_months - b.age_months) : [{ age_months: 0, wholesale_usd: null, retail_usd: null }],
  })
  const set = (patch: Partial<Draft>) => setDraft(d => d ? { ...d, ...patch } : d)
  const setPoint = (i: number, patch: Partial<ValuationPoint>) =>
    setDraft(d => d ? { ...d, points: d.points.map((p, j) => j === i ? { ...p, ...patch } : p) } : d)
  const addPoint = () => setDraft(d => d ? { ...d, points: [...d.points, { age_months: 0, wholesale_usd: null, retail_usd: null }] } : d)
  const rmPoint = (i: number) => setDraft(d => d ? { ...d, points: d.points.filter((_, j) => j !== i) } : d)

  const num = (v: string): number | null => v === '' ? null : Number(v)

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg, color: C.text }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui, sans-serif' }}>Valuation</h1>
        <button onClick={() => setDraft(draft ? null : { ...blank })} style={btn(C.amber, '#1a1300')}><Plus size={14} /> New schedule</button>
      </div>
      <p style={{ margin: '0 0 18px', fontSize: 12, color: C.faint }}>
        Age-based price schedules. A growable's value steps up as it ages and holds until the next breakpoint —
        e.g. a mango graft at 5mo = $30/$50, at 18mo = $100/$160, at 3yr = $200/$300.
        Most specific schedule wins: variety &gt; category &gt; kind default.
      </p>

      {draft && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18, marginBottom: 18 }}>
          <h2 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 600 }}>{draft.id ? 'Edit schedule' : 'New schedule'}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <Field label="Name *"><input value={draft.name} onChange={e => set({ name: e.target.value })} style={input} placeholder="Mango graft" /></Field>
            <Field label="Item kind">
              <select value={draft.item_kind} onChange={e => set({ item_kind: e.target.value as Draft['item_kind'] })} style={input}>
                <option value="plant">Plant / graft (sell the plant)</option>
                <option value="fruit">Fruit (per-unit)</option>
              </select>
            </Field>
            <Field label="Variety (most specific)">
              <select value={draft.variety_id} onChange={e => set({ variety_id: e.target.value })} style={input}>
                <option value="">— any —</option>
                {(varieties.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
            <Field label="Category (if no variety)"><input value={draft.category} onChange={e => set({ category: e.target.value })} style={input} placeholder="mango" /></Field>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>Age breakpoints</label>
              <button type="button" onClick={addPoint} style={btn(C.card, C.amber)}><Plus size={12} /> add row</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ color: C.muted, textAlign: 'left', fontSize: 11 }}>
                <th style={th}>Age (months)</th><th style={th}>Wholesale $</th><th style={th}>Retail $</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {draft.points.map((p, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={td}><input type="number" min={0} value={p.age_months} onChange={e => setPoint(i, { age_months: Number(e.target.value) })} style={{ ...input, width: 100 }} /></td>
                    <td style={td}><input type="number" min={0} step="0.01" value={p.wholesale_usd ?? ''} onChange={e => setPoint(i, { wholesale_usd: num(e.target.value) })} style={{ ...input, width: 120 }} placeholder="—" /></td>
                    <td style={td}><input type="number" min={0} step="0.01" value={p.retail_usd ?? ''} onChange={e => setPoint(i, { retail_usd: num(e.target.value) })} style={{ ...input, width: 120 }} placeholder="—" /></td>
                    <td style={td}>{draft.points.length > 1 && <Trash2 size={14} style={{ cursor: 'pointer', color: C.faint }} onClick={() => rmPoint(i)} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button disabled={!draft.name || save.isPending} onClick={() => save.mutate(draft)} style={btn(C.green, '#fff')}>{save.isPending ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setDraft(null)} style={btn(C.card, C.text)}>Cancel</button>
            {save.isError && <span style={{ color: C.red, fontSize: 12, alignSelf: 'center' }}>{apiError(save.error, 'Save failed')}</span>}
          </div>
        </div>
      )}

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 4 }}>
        {(schedules.data?.length ?? 0) === 0 ? (
          <p style={{ color: C.faint, fontSize: 13, padding: 16, margin: 0 }}>No valuation schedules yet. Create one to price your growables by age.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ color: C.muted, textAlign: 'left', fontSize: 11 }}>
              <th style={th}>Schedule</th><th style={th}>Kind</th><th style={th}>Scope</th><th style={th}>Breakpoints</th><th style={thR}></th>
            </tr></thead>
            <tbody>
              {schedules.data!.map(s => {
                const vName = s.variety_id ? (varieties.data ?? []).find((v) => v.id === s.variety_id)?.name : null
                const scope = vName ? `variety: ${vName}` : s.category ? `category: ${s.category}` : 'kind default'
                const pts = [...s.points].sort((a, b) => a.age_months - b.age_months)
                return (
                  <tr key={s.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={td}><DollarSign size={13} style={{ color: C.faint, verticalAlign: -2, marginRight: 6 }} />{s.name}</td>
                    <td style={td}>{s.item_kind}</td>
                    <td style={td}>{scope}</td>
                    <td style={td}>
                      {pts.map((p, i) => (
                        <span key={i} style={chip}>{p.age_months}mo: {p.wholesale_usd != null ? `$${p.wholesale_usd}` : '—'}/{p.retail_usd != null ? `$${p.retail_usd}` : '—'}</span>
                      ))}
                    </td>
                    <td style={{ ...tdR, whiteSpace: 'nowrap' }}>
                      <Pencil size={14} style={{ cursor: 'pointer', color: C.muted, marginRight: 12 }} onClick={() => edit(s)} />
                      <Trash2 size={14} style={{ cursor: 'pointer', color: C.faint }} onClick={() => { if (confirm(`Delete ${s.name}?`)) remove.mutate(s.id) }} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </main>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 11, color: C.muted }}>{label}<div style={{ marginTop: 4 }}>{children}</div></label>
}
const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', color: C.text, fontSize: 13, outline: 'none' }
const btn = (bg: string, fg: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: bg, border: `1px solid ${C.border}`, color: fg, fontSize: 13, fontWeight: 600, cursor: 'pointer' })
const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600 }
const thR: React.CSSProperties = { ...th, textAlign: 'right' }
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' }
const tdR: React.CSSProperties = { ...td, textAlign: 'right' }
const chip: React.CSSProperties = { display: 'inline-block', padding: '2px 7px', marginRight: 5, marginBottom: 3, borderRadius: 99, fontSize: 11, background: 'rgba(91,141,239,0.15)', color: '#9DBDF5', border: '1px solid rgba(91,141,239,0.3)' }
